import React from "react";
import { Button } from "@/components/ui/button";
import { Share2 } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { recordAdminContact } from "@/utils/adminContacts";

interface ShareActivationButtonProps {
  userId?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  userWhatsapp?: string | null;
  plan?: string | null;
  subscriptionEndDate?: string | null;
}

const planLabel = (plan?: string | null) => {
  switch (plan) {
    case "premium": return "Premium (Mensal)";
    case "quarterly": return "Trimestral";
    case "annual": return "Anual";
    case "vip": return "VIP";
    case "basic": return "Básico";
    default: return plan?.toUpperCase() || "Premium";
  }
};

const formatDate = (date?: string | null) => {
  if (!date) return "—";
  try {
    return new Date(date).toLocaleDateString("pt-BR");
  } catch {
    return "—";
  }
};

const onlyDigits = (s?: string | null) => (s || "").replace(/\D/g, "");

const ShareActivationButton = ({
  userId,
  userName,
  userEmail,
  userWhatsapp,
  plan,
  subscriptionEndDate,
}: ShareActivationButtonProps) => {
  const { toast } = useToast();

  const buildMessage = () => {
    const nome = userName || "Cliente";
    // Usar emojis literais (UTF-8) garante máxima compatibilidade com WhatsApp Web/Android/iOS.
    // Evitar sequências keycap (1️⃣) pois caem como � em fontes/dispositivos sem suporte.
    return (
`✅ *Cadastro Ativado com Sucesso!*

Olá, *${nome}*! 👋

Sua assinatura no *IA Palpite Guru* foi ativada com sucesso. 🎉

📋 *Detalhes da Ativação:*
• Plano: *${planLabel(plan)}*
• E-mail: ${userEmail || "—"}
• Válido até: *${formatDate(subscriptionEndDate)}*

🔐 *Como acessar:*
▶️ Acesse: https://aipalpiteguru.mentepoderosa2030.com
▶️ Faça login com seu e-mail e senha cadastrados
▶️ Aproveite todos os recursos premium liberados!

🚀 *Já liberados para você:*
• Palpites IA ilimitados
• Estatísticas premium completas
• Todos os métodos exclusivos
• GURU CHAT liberado (IA que conversa com você)
• Suporte prioritário via WhatsApp

🤖 *NOVIDADE — GURU CHAT*
Uma IA que conversa com você em tempo real:
• Interpreta seu sonho e transforma em palpite
• Sugere o palpite do dia com base nos resultados
• Indica a melhor ferramenta para cada situação
• Tira dúvidas sobre grupos, dezenas, centenas e milhares
👉 https://aipalpiteguru.mentepoderosa2030.com/guru-chat

Qualquer dúvida, é só responder esta mensagem.

Obrigado pela confiança! 🙏
*Equipe IA Palpite Guru*`
    );
  };

  const handleShare = () => {
    const msg = encodeURIComponent(buildMessage());
    const phone = onlyDigits(userWhatsapp);

    // If user has WhatsApp, open direct chat. Otherwise open generic share.
    const url = phone
      ? `https://wa.me/${phone.startsWith("55") ? phone : `55${phone}`}?text=${msg}`
      : `https://wa.me/?text=${msg}`;

    window.open(url, "_blank", "noopener,noreferrer");

    toast({
      title: "WhatsApp aberto",
      description: phone
        ? "Mensagem de confirmação pronta para enviar ao cliente."
        : "Cliente sem WhatsApp cadastrado — escolha o contato manualmente.",
    });

    if (userId) {
      void recordAdminContact({
        targetUserId: userId,
        contactType: "ativacao",
        channel: "whatsapp",
        notes: "Mensagem de confirmação de ativação",
      });
    }
  };

  return (
    <Button
      size="sm"
      variant="ghost"
      onClick={handleShare}
      className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
      title="Compartilhar confirmação de ativação"
    >
      <Share2 size={16} />
    </Button>
  );
};

export default ShareActivationButton;
